import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs
} from 'firebase/firestore';
import Modal from 'react-modal';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

// Formatters
const formatAmount = (amount) => {
  return `₦${(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const formatAmount1 = (amount) => {
  return `N${(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const generateCheckoutId = (firestoreId) => {
  return firestoreId.substring(0, 8).toUpperCase();
};

const AccountStatements = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [allCheckoutEntries, setAllCheckoutEntries] = useState([]);
  const [filter, setFilter] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isAllAccounts, setIsAllAccounts] = useState(false);

  const accountsCollectionRef = collection(db, "accounts");
  const checkoutEntriesCollectionRef = collection(db, "checkoutEntries");
  const hotelName = HOTEL_NAME || "Hotel Name";

  useEffect(() => {
    if (!user || !userData) return;
    const init = async () => {
        await fetchAccounts();
        await fetchAllCheckoutEntries();
        setLoading(false);
    };
    init();
  }, [user, userData]);

  const fetchAccounts = async () => {
    try {
      const snap = await getDocs(accountsCollectionRef);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAccounts(list.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (err) {
      setError("Failed to load accounts");
      setShowModal(true);
    }
  };

  const fetchAllCheckoutEntries = async () => {
    try {
      const snap = await getDocs(checkoutEntriesCollectionRef);
      const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllCheckoutEntries(entries);
      return entries;
    } catch (err) {
      setError("Could not load checkout entries");
      setShowModal(true);
      return [];
    }
  };

  const fetchEntriesForAccount = async (accountId) => {
    setIsFetching(true);
    try {
      const entries = allCheckoutEntries.length ? allCheckoutEntries : await fetchAllCheckoutEntries();
      const selectedAcc = accounts.find(a => a.id === accountId);
      const targetAccountName = selectedAcc?.name?.toLowerCase() || '';

      const filtered = entries.filter(entry => {
        const firstLine = entry.lines?.["0"] || Object.values(entry.lines || {})[0] || {};
        const accountName = (firstLine.accountName || '').toLowerCase();
        return accountName === targetAccountName;
      });

      setLedgerEntries(filtered);
    } catch (err) {
      setError("Failed to load entries");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSelectAccount = (accountId) => {
    setSelectedAccount(accountId);
    setFilter('');
    if (accountId) {
      fetchEntriesForAccount(accountId);
    } else {
      setLedgerEntries([]);
    }
  };

  const getBatchName = (entry) => {
    if (entry.batchName?.trim()) return entry.batchName.trim();
    if (entry.booking?.batchName?.trim()) return entry.booking.batchName.trim();
    return "No Batch";
  };

  // Helper to calculate the actual balance reliably
  const calculateBalance = (entry) => {
    const booking = entry.booking || {};
    const total = Number(booking.totalPrice) || 0;
    const incidentals = Number(booking.incidentalTotal) || 0;
    const deposit = Number(booking.deposit) || 0;
    
    // If a hardcoded balance exists and is non-zero, we can consider it, 
    // but the manual calculation is safer for "Paid/Unpaid" logic.
    return (total + incidentals) - deposit;
  };

  const generateLedgerPDF = (reportType) => {
    if (!startDate || !endDate) {
      setError("Select both start and end dates");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : "Unknown";
    let y = 40;

    const currentAccountName = accounts.find(a => a.id === selectedAccount)?.name || "Unknown_Account";    
    const sanitizedName = currentAccountName.replace(/[^a-z0-9]/gi, '_');

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const dateFiltered = allCheckoutEntries.filter(entry => {
      const d = new Date(entry.date);
      return d >= start && d <= end;
    });

    if (dateFiltered.length === 0) {
        setError("No records found for selected range.");
        setShowModal(true);
        return;
    }

    const renderAccountHeader = (title) => {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(14).setFont("helvetica", "bold").setTextColor(41, 128, 185).text(title, 14, y);
        doc.setTextColor(0);
        y += 5;
    };

    const processTable = (entries, subTitle) => {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(11).setFont("helvetica", "bold").text(subTitle, 14, y);
      y += 2;

      const tableData = entries.map(entry => {
        const booking = entry.booking || {};
        const total = Number(booking.totalPrice) || 0;
        const paid = Number(booking.deposit) || 0;
        const balance = calculateBalance(entry);
        return [
          entry.checkoutId || generateCheckoutId(entry.id),
          dateFormatter.format(new Date(entry.date)),
          booking.guestName || '—',
          `${booking.roomNumber || '—'}`,
          formatAmount1(total),
          formatAmount1(paid),
          formatAmount1(balance)
        ];
      });

      autoTable(doc, {
        startY: y,
        head: [['ID', 'Date', 'Guest', 'Room', 'Total', 'Paid', 'Balance']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 8, textColor: 0 },
        margin: { left: 14, right: 14 }
      });

      y = doc.lastAutoTable.finalY + 5;
    };

    doc.addImage(Logo, "PNG", 14, 10, 15, 15);
    doc.setFontSize(16).setFont("helvetica", "bold").text(hotelName, 35, 15);
    doc.setFontSize(10).setFont("helvetica", "normal").text(`Generated by: ${fullName} | Period: ${startDate} to ${endDate}`, 35, 22);
    y = 10;

    if (isAllAccounts) {
      const byAccount = {};
      dateFiltered.forEach(e => {
        const accName = e.lines?.["0"]?.accountName || "Unknown Account";
        if (!byAccount[accName]) byAccount[accName] = [];
        byAccount[accName].push(e);
      });
      y = 32;
      Object.entries(byAccount).forEach(([accName, accEntries]) => {        
        renderAccountHeader(`ACCOUNT: ${accName}`);
        if (reportType === 'batch') {
          const byBatch = {};
          accEntries.forEach(e => {
             const b = getBatchName(e);
             if (!byBatch[b]) byBatch[b] = [];
             byBatch[b].push(e);
          });
          Object.entries(byBatch).forEach(([bn, entries]) => processTable(entries, bn));
        } else {
          // STRICT FILTERING: Paid <= 0 | Unpaid > 0
          const paidEntries = accEntries.filter(e => calculateBalance(e) <= 0);
          const unpaidEntries = accEntries.filter(e => calculateBalance(e) > 0);
          
          if (paidEntries.length) processTable(paidEntries, "PAID");
          if (unpaidEntries.length) processTable(unpaidEntries, "UNPAID");
        }
      });
    } else {
      const accName = accounts.find(a => a.id === selectedAccount)?.name;
      const accountEntries = dateFiltered.filter(e => (e.lines?.["0"]?.accountName || '') === accName);
      y = 32;
      renderAccountHeader(`ACCOUNT STATEMENT: ${accName}`);

      if (reportType === 'batch') {
        const byBatch = {};
        accountEntries.forEach(e => {
           const b = getBatchName(e);
           if (!byBatch[b]) byBatch[b] = [];
           byBatch[b].push(e);
        });
        Object.entries(byBatch).forEach(([bn, entries]) => processTable(entries, bn));
      } else {
        const paidEntries = accountEntries.filter(e => calculateBalance(e) <= 0);
        const unpaidEntries = accountEntries.filter(e => calculateBalance(e) > 0);
        
        if (paidEntries.length) processTable(paidEntries, "PAID");
        if (unpaidEntries.length) processTable(unpaidEntries, "UNPAID");
      }
    }

    const fileName = isAllAccounts 
      ? `Statement_All_Accounts_${startDate}.pdf` 
      : `Statement_${sanitizedName}_${startDate}.pdf`;

    doc.save(fileName);

    // doc.save(`Account Statement_${isAllAccounts ? 'All' : 'Single'}_${startDate}.pdf`);
    setIsDateRangeModalOpen(false);
  };

  const filteredEntries = useMemo(() => {
    if (!filter) return ledgerEntries;
    const term = filter.toLowerCase();
    return ledgerEntries.filter(entry => 
      entry.booking?.guestName?.toLowerCase().includes(term) ||
      entry.checkoutId?.toLowerCase().includes(term)
    );
  }, [ledgerEntries, filter]);

  if (loading || !userData) return <Loader text="LOADING" />;
  const allowed = ['Accountant', 'Admin', 'Supreme'].includes(userData.status);

  return (
    <>
      {allowed ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Account Statements</h2>
            <div onClick={() => selectedAccount && fetchEntriesForAccount(selectedAccount)} style={{ cursor: 'pointer' }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </div>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            <div className="space-between align-center gap-15" style={{ width: '100%', maxWidth: '600px' }}>
              <select
                className="input-field"
                style={{ marginTop: '10px', marginBottom: '-10px' }}
                value={selectedAccount || ''}
                onChange={(e) => handleSelectAccount(e.target.value)}
              >
                <option value="">Select Account</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>

              <div 
                onClick={() => { setIsAllAccounts(true); setIsDateRangeModalOpen(true); }}
                style={{ marginTop: '10px', marginBottom: '-10px' }}
                className="list-btn"
              >
                <i className="fas fa-file-download"></i>
              </div>
            </div>

            {selectedAccount && (
              <div className="row justify-center align-center gap-10" style={{ margin: '15px 0' }}>
                <button 
                  className="login-button caps" 
                  onClick={() => { setIsAllAccounts(false); setIsDateRangeModalOpen(true); }}
                >
                  Download Statement
                </button>
              </div>
            )}

            {selectedAccount && (
              <>
                <input
                  type="text"
                  className="input-field1"
                  placeholder="Filter by guest, room, date or checkout ID..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />

                <div className="scroll-fixed scroll-fixed1">
                  <div className="class-list class-list-d scrollable">
                    {isFetching ? (
                      <p className="loading-text">Loading entries...</p>
                    ) : filteredEntries.length === 0 ? (
                      <p className="loading-text">No entries found for this account.</p>
                    ) : (
                      filteredEntries.map((entry, index) => {
                        const booking = entry.booking || {};
                        const balance = calculateBalance(entry);

                        return (
                          <div 
                            key={index} 
                            className="class-item" 
                            style={{ 
                              padding: '14px', 
                              marginBottom: '12px', 
                              borderRadius: '8px', 
                              background: '#1e1e1e',
                              border: '1px solid #333'
                            }}
                          >
                            <div className="column">
                              <div>
                                <strong style={{ fontSize: '1.1em' }}>
                                    {booking.guestName || '—'}
                                    {booking.batchName && booking.batchName !== 'No Batch' && ` (${booking.batchName})`}
                                </strong><br />
                                <span>Room: <strong>{booking.roomNumber || '—'}</strong> ({booking.roomTypeName || '—'})</span><br />
                                <span>Checkout ID: <strong>{entry.checkoutId || generateCheckoutId(entry.id)}</strong></span><br />                                
                              </div>                              
                              <div>
                                <span>Total: <strong>{formatAmount(booking.totalPrice)}</strong></span><br />
                                <span>Paid: <strong>{formatAmount(booking.deposit)}</strong></span><br />
                                <span>Balance: <strong className="font-gold">
                                  {formatAmount(balance)}
                                </strong></span>
                              </div>
                              <div>
                                <span className="size5 font-gold bold">{dateFormatter.format(new Date(entry.date))}</span>
                              </div>
                            </div>
                            <button
                              className="list-btn"
                              onClick={() => { setSelectedEntry(entry); setIsDetailModalOpen(true); }}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <Modal
            isOpen={isDateRangeModalOpen}
            onRequestClose={() => setIsDateRangeModalOpen(false)}
            className="custom-modal"
            overlayClassName="custom-modal-overlay"
          >
            <h2 className="size5 caps bold">Select Date Range</h2>
            <div className="modal-body">
              <label className="input-label">From Date</label>
              <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <label className="input-label">To Date</label>
              <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              
              <div className="row align-center justify-center gap-15">
                <button className="login-button" onClick={() => generateLedgerPDF('batch')}>Batch</button>
                <button className="login-button" onClick={() => generateLedgerPDF('paid-unpaid')}>Paid/Unpaid</button>
              </div>
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button className="login-button" onClick={() => {
                  setIsDateRangeModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setIsAllAccounts(false);
                }}>
                  Cancel
                </button>
              </div>
            </div>
          </Modal>

          <Modal
            isOpen={isDetailModalOpen}
            onRequestClose={() => setIsDetailModalOpen(false)}
            className="custom-modal"
            overlayClassName="custom-modal-overlay"
          >
            <h2 className="size5 caps bold">Entry Details</h2>
            {selectedEntry && (
              <div className="modal-scroll">
                <p><strong>Checkout ID:</strong> {selectedEntry.checkoutId || generateCheckoutId(selectedEntry.id)}</p>
                <p><strong>Guest:</strong> {selectedEntry.booking?.guestName}</p>
                <p><strong>Balance:</strong> {formatAmount(calculateBalance(selectedEntry))}</p>
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button className="login-button" onClick={() => setIsDetailModalOpen(false)}>Close</button>
                </div>
              </div>
            )}
          </Modal>

          {showModal && (
            <SuccessModal icon="xmark" iconColor="red" message={error} onClose={() => { setError(""); setShowModal(false); }} />
          )}
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
    </>
  );
};

export default AccountStatements;